// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility, {
	ZOOM_LEVEL_MARKERS,
	ZOOM_LEVEL_MAX,
	ZOOM_LEVEL_MIN,
} from '@app/features/accessibility/state/Accessibility';
import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {APP_ZOOM_LEVEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import Keybind from '@app/features/input/state/InputKeybind';
import {formatKeyCombo} from '@app/features/input/utils/KeybindUtils';
import {Slider} from '@app/features/ui/components/Slider';
import {shouldWarnAboutFirefoxWebZoomShortcuts} from '@app/features/ui/utils/AppZoomKeybindUtils';
import {formatRoundedPercentage, roundPercentage} from '@app/features/ui/utils/PercentageFormatting';
import {msg, ph} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

const CHAT_FONT_SIZE_DESCRIPTOR = msg({
	message: 'Chat font size',
	comment: 'Accessible label for the chat font scaling slider.',
});
const PIXELS_DESCRIPTOR = msg({
	message: '{fontSize} pixels',
	comment: 'Accessible value for the chat font scaling slider.',
});
const RESET_FONT_SIZE_DESCRIPTOR = msg({
	message: 'Reset font size',
	comment: 'Button below the chat font scaling slider. Restores chat font size to the default.',
});
const PERCENT_DESCRIPTOR = msg({
	message: '{zoomPercent} percent',
	comment: 'Accessible value for the app zoom level slider.',
});
const RESET_ZOOM_DESCRIPTOR = msg({
	message: 'Reset zoom',
	comment: 'Label for controls that reset app zoom to 100%, including the app zoom slider button.',
});
const ADJUST_THE_OVERALL_ZOOM_LEVEL_OF_THE_APP_DESCRIPTOR = msg({
	message:
		'Adjust the overall zoom level of the app. Firefox keeps the standard browser zoom shortcuts, so use the slider here for {productName} zoom.',
	comment: 'Description for the app zoom setting in Firefox on the web. productName is the app name.',
});
const ADJUST_THE_OVERALL_ZOOM_LEVEL_OF_THE_APP_2_DESCRIPTOR = msg({
	message: 'Adjust the overall zoom level of the app. Use {zoomIn} / {zoomOut} to adjust quickly.',
	comment: 'Description for the app zoom setting. The placeholders are keyboard shortcuts.',
});
const DEFAULT_FONT_SIZE = 16;
const DEFAULT_ZOOM_PERCENT = 100;
export const FontSizeTabContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	const fontSize = Accessibility.fontSize;
	return (
		<Slider
			defaultValue={fontSize}
			factoryDefaultValue={DEFAULT_FONT_SIZE}
			ariaLabel={i18n._(CHAT_FONT_SIZE_DESCRIPTOR)}
			ariaValueText={i18n._(PIXELS_DESCRIPTOR, {fontSize})}
			markers={[12, 14, 15, 16, 18, 20, 24]}
			stickToMarkers={true}
			onValueChange={(value) => AccessibilityCommands.update({fontSize: value})}
			onMarkerRender={(value) => `${value}px`}
			showResetButton={true}
			resetButtonPlacement="below"
			resetLabel={i18n._(RESET_FONT_SIZE_DESCRIPTOR)}
			data-flx="user.appearance-tab.scaling-tab.font-size-tab-content.slider"
		/>
	);
});
export const AppZoomLevelTabContent: React.FC = observer(() => {
	const zoomLevel = Accessibility.zoomLevel;
	const zoomPercent = roundPercentage(zoomLevel * 100);
	const markers = ZOOM_LEVEL_MARKERS.map((step) => roundPercentage(step * 100));
	const isOnMarker = markers.includes(zoomPercent);
	const {i18n} = useLingui();
	return (
		<Slider
			defaultValue={zoomPercent}
			factoryDefaultValue={DEFAULT_ZOOM_PERCENT}
			minValue={Math.round(ZOOM_LEVEL_MIN * 100)}
			maxValue={Math.round(ZOOM_LEVEL_MAX * 100)}
			ariaLabel={i18n._(APP_ZOOM_LEVEL_DESCRIPTOR)}
			ariaValueText={i18n._(PERCENT_DESCRIPTOR, {zoomPercent})}
			step={1}
			markers={markers}
			stickToMarkers={false}
			asValueChanges={() => {}}
			onValueChange={(value) => AccessibilityCommands.update({zoomLevel: value / 100})}
			onMarkerRender={formatRoundedPercentage}
			onValueRender={(value) => <Trans>{ph({zoomPercent: roundPercentage(value)})}%</Trans>}
			showResetButton={true}
			resetButtonPlacement="below"
			resetLabel={i18n._(RESET_ZOOM_DESCRIPTOR)}
			resetAccessory={
				!isOnMarker ? (
					<Trans comment="Status text beside the app zoom reset button when the current zoom value is between the marked values.">
						Current: {ph({zoomPercent})}%
					</Trans>
				) : undefined
			}
			data-flx="user.appearance-tab.scaling-tab.app-zoom-level-tab-content.slider"
		/>
	);
});

export function useAppZoomLevelDescription(): string {
	const {i18n} = useLingui();
	return useMemo(() => {
		if (shouldWarnAboutFirefoxWebZoomShortcuts()) {
			return i18n._(ADJUST_THE_OVERALL_ZOOM_LEVEL_OF_THE_APP_DESCRIPTOR, {productName: PRODUCT_NAME});
		}
		const zoomIn = formatKeyCombo(Keybind.getByAction('system_zoom_in').combo);
		const zoomOut = formatKeyCombo(Keybind.getByAction('system_zoom_out').combo);
		return i18n._(ADJUST_THE_OVERALL_ZOOM_LEVEL_OF_THE_APP_2_DESCRIPTOR, {zoomIn, zoomOut});
	}, [i18n]);
}
